/**
 * Email Service
 * Handles sending emails via Cloudflare Worker
 */

const EMAIL_WORKER_URL = import.meta.env.VITE_EMAIL_WORKER_URL || 'https://rapidfix-email-worker.bhrgvkrishna354.workers.dev';

interface SendDeletionEmailParams {
  email: string;
  userName: string;
  daysRemaining?: number;
  token?: string;
}

interface SendCancellationEmailParams {
  email: string;
  userName: string;
  token?: string;
}

interface SendCustomEmailParams {
  email: string;
  subject: string;
  html: string;
  text?: string;
  userName?: string;
  token?: string;
}

interface EmailResponse {
  success: boolean;
  id?: string;
  error?: string;
}

/**
 * Send custom email with HTML content
 */
export async function sendCustomEmail(
  email: string,
  subject: string,
  html: string,
  text?: string,
  userName?: string,
  token?: string
): Promise<EmailResponse> {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${EMAIL_WORKER_URL}/send-custom-email`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        email,
        subject,
        html,
        text: text || subject,
        userName: userName || 'User',
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send email');
    }

    return data;
  } catch (error) {
    console.error('Failed to send custom email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Send account deletion confirmation email
 */
export async function sendDeletionEmail(params: SendDeletionEmailParams): Promise<EmailResponse> {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (params.token) {
      headers['Authorization'] = `Bearer ${params.token}`;
    }

    const response = await fetch(`${EMAIL_WORKER_URL}/send-deletion-email`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        email: params.email,
        userName: params.userName,
        daysRemaining: params.daysRemaining || 30,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send email');
    }

    return data;
  } catch (error) {
    console.error('Failed to send deletion email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Send account deletion cancellation email
 */
export async function sendCancellationEmail(params: SendCancellationEmailParams): Promise<EmailResponse> {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (params.token) {
      headers['Authorization'] = `Bearer ${params.token}`;
    }

    const response = await fetch(`${EMAIL_WORKER_URL}/send-cancellation-email`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        email: params.email,
        userName: params.userName,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send email');
    }

    return data;
  } catch (error) {
    console.error('Failed to send cancellation email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
